import {
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { PlanFinding } from '@gfe/core/models/plan-finding.model';
import { CurrentLangService } from '@gfe/core/services/common/current-lang.service';
import { EntityTypeLabels } from '@gfe/core/services/gbe-error-parser/entity-ref-labels';
import { GbeErrorParserService } from '@gfe/core/services/gbe-error-parser/gbe-error-parser.service';
import { FindingSize } from '@gfe/core/services/gbe-error-parser/warning.model';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import { formatCompactSeconds } from '@gfe/core/utils/time-utils';
import { commonFindingDay } from '@gfe/shared/components/finding-message/finding-display';
import { FindingMessageComponent } from '@gfe/shared/components/finding-message/finding-message.component';
import { TooltipComponent } from '@gfe/shared/components/tooltip/tooltip.component';

import { FindingCopyService } from '@core/services/finding-copy/finding-copy.service';
import { UserPreferencesActions } from '@core/store/user-preferences/user-preferences.actions';
import {
  selectFindingsListFolded,
  selectFindingsListPillsOpen
} from '@core/store/user-preferences/user-preferences.selectors';

import { ButtonComponent } from '@shared/components/button/button.component';

import { FindingCardPopupService } from '../finding-card/finding-card-popup.service';
import {
  FilterableGroup,
  GroupedOccurrence,
  OccurrenceFilter,
  byBlockingThenCount,
  narrowFindings,
  pickFindings
} from './findings-filtering';

/** Identical occurrences are told apart by their order among themselves. */
function keyOccurrences(occurrences: GroupedOccurrence[]): void {
  const seen = new Map<string, number>();
  for (const occurrence of occurrences) {
    const content = JSON.stringify([
      occurrence.body,
      occurrence.refs.map((ref) => ref.uid)
    ]);
    const repeat = seen.get(content) ?? 0;
    seen.set(content, repeat + 1);
    occurrence.key = `${repeat}${content}`;
  }
}

@Component({
  selector: 'app-plan-findings-panel',
  imports: [TranslatePipe, ButtonComponent, TooltipComponent, FindingMessageComponent],
  templateUrl: './plan-findings-panel.component.html',
  styles: ``
})
export class PlanFindingsPanelComponent {
  readonly findings = input.required<PlanFinding[]>();
  readonly entityTypeLabels = input.required<EntityTypeLabels>();
  /**
   * The plan's status is not known yet — the first read is in flight, or a check is running.
   * Distinct from an empty `findings`, which means the answer arrived and was "nothing found".
   */
  readonly loading = input<boolean>(false);
  readonly planNeverChecked = input<boolean>(false);
  readonly findingsAreStale = input<boolean>(false);
  readonly checkFailed = input<boolean>(false);
  /** The plan has nothing to be checked against, so no check can be offered. */
  readonly unavailable = input<boolean>(false);
  readonly live = input<boolean>(false);
  /** Occurrences it rejects are left out of the list and counted in the hidden note. */
  readonly occurrenceFilter = input<OccurrenceFilter | null>(null);
  /** Rendered under the list with `{ count }` of the occurrences the filter hid, when any. */
  readonly hiddenNoteKey = input<string | null>(null);
  /** Opens the list from the start instead of folded behind the status line's caret. */
  readonly startExpanded = input<boolean>(false);
  /**
   * Caps the open list's height so it scrolls on its own. Off where the container already
   * scrolls, which would otherwise nest a second scrollbar inside it.
   */
  readonly listScrolls = input<boolean>(true);
  /** Off where the host draws the frame, so the list adds no border or background of its own. */
  readonly framed = input<boolean>(true);
  readonly checkRequested = output<void>();
  /** Emitted once the list has cleared its own filters, so the host can reset its scope. */
  readonly filtersCleared = output<void>();

  protected readonly ICONS = ICONS;

  private readonly gbeErrorParserService = inject(GbeErrorParserService);
  private readonly currentLangService = inject(CurrentLangService);
  private readonly findingCopy = inject(FindingCopyService);
  private readonly findingCardPopup = inject(FindingCardPopupService);
  private readonly store = inject(Store);

  protected readonly currentLang = this.currentLangService.currentLang;

  protected readonly expanded = linkedSignal(() => this.startExpanded());
  protected readonly query = signal('');
  protected readonly pillsOpen = this.store.selectSignal(selectFindingsListPillsOpen);
  private readonly savedFolds = this.store.selectSignal(selectFindingsListFolded);
  /** Codes whose rows past the first few the reader asked to see. */
  private readonly revealed = signal<ReadonlySet<string>>(new Set());
  /** Folds made while searching; they last as long as the query, so saved folds return after it. */
  private readonly searchFolds = linkedSignal<string, ReadonlySet<string>>({
    source: this.query,
    computation: () => new Set()
  });

  protected readonly firstRows = 3;

  /** Each view keeps the finding it came from: the inner errors of `multiple_errors` carry other codes. */
  private readonly findingViews = computed(() => {
    this.currentLang();
    return this.findings().flatMap((finding) =>
      this.gbeErrorParserService
        .resolve(finding)
        .map((view) => ({ view, blocksOptimization: finding.blocksOptimization }))
    );
  });

  protected readonly findingBreakdown = computed(() => {
    const groups = new Map<string, FilterableGroup>();

    for (const { view, blocksOptimization } of this.findingViews()) {
      const occurrences = view.occurrences.map((occurrence) => ({
        ...occurrence,
        raw: view.raw,
        blocksOptimization,
        key: ''
      }));
      const entry = groups.get(view.code);
      if (entry) {
        entry.occurrences.push(...occurrences);
        entry.blocksOptimization ||= blocksOptimization;
      } else {
        groups.set(view.code, {
          code: view.code,
          label: view.title,
          blocksOptimization,
          occurrences
        });
      }
    }

    for (const group of groups.values()) keyOccurrences(group.occurrences);
    return [...groups.values()].sort(byBlockingThenCount);
  });

  /** Taken over every finding, so the search reads times the same whatever it keeps. */
  protected readonly sameDay = computed(() =>
    commonFindingDay(this.findingBreakdown().flatMap((group) => group.occurrences))
  );

  private readonly narrowed = computed(() =>
    narrowFindings({
      groups: this.findingBreakdown(),
      query: this.query(),
      scope: this.occurrenceFilter(),
      sameDay: this.sameDay()
    })
  );

  /**
   * Only codes the plan's findings still hold: a pick kept for a code that left would narrow
   * the list again, unasked, once that code came back. A pick the search or the scope hides
   * stays, and applies again once they let its code through.
   */
  protected readonly picked = linkedSignal<FilterableGroup[], ReadonlySet<string>>({
    source: this.findingBreakdown,
    computation: (groups, previous) => {
      const codes = new Set(groups.map((group) => group.code));
      return new Set([...(previous?.value ?? [])].filter((code) => codes.has(code)));
    }
  });

  protected readonly filtered = computed(() =>
    pickFindings(this.narrowed(), this.picked())
  );

  /**
   * A kind the filters left nothing of is dropped, so the line never reads "0 of N" about
   * what the filters hid; when they hid everything, one zero label keeps the count.
   */
  protected readonly status = computed(() => {
    const { shown, total, shownBlocking, totalBlocking } = this.filtered();
    const blocking = { count: shownBlocking, total: totalBlocking };
    const warnings = { count: shown - shownBlocking, total: total - totalBlocking };
    return {
      blocking,
      warnings,
      showBlocking:
        blocking.count > 0 || (shown === 0 && warnings.total === 0 && total > 0),
      showWarnings: warnings.count > 0 || (shown === 0 && warnings.total > 0)
    };
  });

  /** A host passes `occurrenceFilter` only while its own scope narrows the list. */
  protected readonly filtersActive = computed(
    () =>
      this.findings().length > 0 &&
      (this.query().trim() !== '' ||
        this.picked().size > 0 ||
        this.occurrenceFilter() !== null)
  );

  protected readonly foldable = computed(
    () => !this.loading() && this.findings().length > 0
  );

  protected readonly mark = computed(() => {
    if (this.loading()) {
      return {
        tone: 'bg-interactive text-text-tertiary',
        icon: `${ICONS.arrowsClockwise} animate-spin`
      };
    }
    if (this.filtered().totalBlocking > 0)
      return { tone: 'bg-danger text-white', icon: ICONS.prohibit };
    if (this.findings().length > 0)
      return { tone: 'bg-warning text-white', icon: ICONS.warning };
    if (this.planNeverChecked() || this.unavailable())
      return { tone: 'bg-interactive text-text-tertiary', icon: ICONS.info };
    return { tone: 'bg-interactive text-text-secondary', icon: ICONS.checkCircle };
  });

  protected readonly pickedCount = computed(
    () => this.filtered().available.filter((group) => this.picked().has(group.code)).length
  );

  /**
   * A search unfolds every category it kept. Otherwise a saved fold wins, and a code with
   * none folds unless it heads the list.
   */
  protected readonly unfolded = computed(() => {
    const groups = this.filtered().groups;
    if (this.query().trim() !== '') {
      const folded = this.searchFolds();
      return new Set(groups.map((group) => group.code).filter((code) => !folded.has(code)));
    }
    const saved = this.savedFolds();
    return new Set(
      groups
        .filter(
          (group, index) =>
            saved[group.code] === false || (index === 0 && !(group.code in saved))
        )
        .map((group) => group.code)
    );
  });

  protected isRevealed(code: string): boolean {
    return this.revealed().has(code);
  }

  /** Rows are sorted largest first, so the first sized row holds the group's worst. */
  protected worstSize(group: FilterableGroup): FindingSize | undefined {
    return group.occurrences.find((occurrence) => occurrence.size)?.size;
  }

  protected compactSeconds(seconds: number): string {
    return formatCompactSeconds(seconds);
  }

  protected share(group: FilterableGroup): number {
    const total = this.filtered().total;
    return total === 0 ? 0 : (group.occurrences.length / total) * 100;
  }

  /**
   * The row is the anchor as well as the trigger, so the card opens against the line the
   * reader clicked rather than against the panel. Toggles closed on a second click of the
   * same row, matching the timeline's step-popup gesture.
   */
  protected openFindingCard(
    group: { code: string; label: string },
    occurrence: GroupedOccurrence,
    event: Event
  ): void {
    const anchorEl = event.currentTarget as HTMLElement;
    if (this.findingCardPopup.isOpenFor(anchorEl)) {
      this.findingCardPopup.close();
      return;
    }
    this.findingCardPopup.open(anchorEl, {
      title: group.label,
      code: group.code,
      occurrence,
      raw: occurrence.raw,
      typeLabels: this.entityTypeLabels()
    });
  }

  /** The search row sits above the list, so a search into a folded list would show nothing. */
  protected onSearch(event: Event): void {
    const startsSearching = this.query().trim() === '';
    const query = (event.target as HTMLInputElement).value;
    this.query.set(query);
    if (startsSearching && query.trim() !== '') this.expanded.set(true);
  }

  protected clearSearch(input: HTMLInputElement): void {
    this.query.set('');
    input.focus();
  }

  protected clearFilters(): void {
    this.query.set('');
    this.picked.set(new Set());
    this.filtersCleared.emit();
  }

  protected toggleExpanded(): void {
    this.expanded.set(!this.expanded());
  }

  protected toggleGroup(code: string): void {
    const unfolded = this.unfolded().has(code);
    if (this.query().trim() !== '') {
      const next = new Set(this.searchFolds());
      if (unfolded) next.add(code);
      else next.delete(code);
      this.searchFolds.set(next);
      return;
    }
    this.store.dispatch(
      UserPreferencesActions.setFindingsListPreferences({
        preferences: { folded: { [code]: unfolded } }
      })
    );
  }

  protected togglePills(): void {
    this.store.dispatch(
      UserPreferencesActions.setFindingsListPreferences({
        preferences: { pillsOpen: !this.pillsOpen() }
      })
    );
  }

  protected togglePick(code: string): void {
    const next = new Set(this.picked());
    if (next.has(code)) next.delete(code);
    else next.add(code);
    this.picked.set(next);
  }

  protected reveal(code: string): void {
    this.revealed.set(new Set(this.revealed()).add(code));
  }

  protected copyFindingMessage(occurrence: GroupedOccurrence, event: MouseEvent): void {
    this.findingCopy.copy(occurrence, occurrence.raw, event.shiftKey);
  }

  protected copyFindingHeading(group: { code: string; label: string }): void {
    this.findingCopy.copyHeading(group.label, group.code);
  }
}
